import { useState, useEffect, useCallback } from 'react';

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

// Create a helper function to check if we're in the browser environment
const isBrowser = typeof window !== 'undefined';

const useResponsive = () => {
  // Initialize state with default values for server-side rendering
  const [windowSize, setWindowSize] = useState({
    width: isBrowser ? window.innerWidth : 0,
    height: isBrowser ? window.innerHeight : 0
  });
  
  const handleResize = useCallback(() => {
    if (!isBrowser) return;
    
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    
    window.addEventListener('resize', handleResize);
    
    // Initial call to ensure correct values on mount
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // Determine current breakpoint
  const currentBreakpoint = (): Breakpoint => {
    const { width } = windowSize;
    if (width >= breakpoints['2xl']) return '2xl';
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    if (width >= breakpoints.sm) return 'sm';
    return 'xs';
  };

  // Check if screen is at least a certain breakpoint
  const isAtLeast = (breakpoint: Breakpoint): boolean => {
    return windowSize.width >= breakpoints[breakpoint];
  };

  // Check if screen is smaller than a certain breakpoint
  const isSmallerThan = (breakpoint: Breakpoint): boolean => {
    return windowSize.width < breakpoints[breakpoint];
  };

  // Check if screen is between two breakpoints
  const isBetween = (minBreakpoint: Breakpoint, maxBreakpoint: Breakpoint): boolean => {
    return (
      windowSize.width >= breakpoints[minBreakpoint] &&
      windowSize.width < breakpoints[maxBreakpoint]
    );
  };

  // Check if the device is in portrait orientation
  const isPortrait = (): boolean => {
    return windowSize.height > windowSize.width;
  };

  // Check if the device is in landscape orientation
  const isLandscape = (): boolean => {
    return windowSize.width > windowSize.height;
  };

  return {
    windowSize,
    breakpoint: currentBreakpoint(),
    isAtLeast,
    isSmallerThan,
    isBetween,
    isPortrait,
    isLandscape
  };
};

export default useResponsive; 